import { useState, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";

interface User {
  id: string;
  full_name: string | null;
  email: string | null;
  status: string;
}

interface Props {
  selectedUsers: User[];
  onClose: () => void;
  onSaved: () => void;
}

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

const STATUS_OPTIONS = [
  { value: "active", label: "활성", desc: "정상 사용 가능", icon: "ri-check-line", color: "text-emerald-400", border: "border-emerald-500/30", bg: "bg-emerald-500/10" },
  { value: "inactive", label: "비활성", desc: "로그인 가능, 발송 불가", icon: "ri-pause-line", color: "text-gray-400", border: "border-white/[0.1]", bg: "bg-white/[0.04]" },
  { value: "banned", label: "정지", desc: "모든 기능 차단", icon: "ri-forbid-line", color: "text-red-400", border: "border-red-500/30", bg: "bg-red-500/10" },
  { value: "pending", label: "승인 대기", desc: "관리자 승인 필요", icon: "ri-time-line", color: "text-amber-400", border: "border-amber-500/30", bg: "bg-amber-500/10" },
];

export default function BulkStatusModal({ selectedUsers, onClose, onSaved }: Props) {
  const { session } = useAuth();
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const handleSave = useCallback(async () => {
    setError(null);
    setSaved(false);
    const token = session?.access_token;
    if (!token) {
      setError("로그인이 필요합니다.");
      return;
    }

    if (!selectedStatus) {
      setError("변경할 상태를 선택하세요.");
      return;
    }

    const userIds = selectedUsers.map((u) => u.id);
    if (userIds.length === 0) {
      setError("선택된 회원이 없습니다.");
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/update-user-status`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: SUPABASE_ANON_KEY,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_ids: userIds,
          status: selectedStatus,
        }),
      });
      const resBody = await res.json().catch(() => null);
      if (!res.ok) {
        console.error("[bulkStatus] response error:", resBody);
        throw new Error(resBody?.error || `HTTP ${res.status}`);
      }
      setSaved(true);
      setTimeout(() => {
        onSaved();
        onClose();
      }, 1200);
    } catch (err: any) {
      console.error("[bulkStatus] catch error:", err);
      setError(err.message || "저장 중 오류가 발생했습니다.");
    } finally {
      setSaving(false);
    }
  }, [session, selectedStatus, selectedUsers, onSaved, onClose]);

  const displayUsers = selectedUsers.slice(0, 5);
  const remaining = selectedUsers.length - displayUsers.length;
  const selectedLabel = STATUS_OPTIONS.find((o) => o.value === selectedStatus)?.label ?? "";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>

      <div className="relative w-full max-w-md rounded-2xl border border-white/[0.08] bg-[#0c0c1a] shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.08] shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-500/10">
              <i className="ri-shield-user-line text-emerald-400 text-sm"></i>
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm">일괄 상태 변경</h3>
              <p className="text-gray-500 text-xs">{selectedUsers.length.toLocaleString()}명의 회원에게 적용</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.06] cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Selected users preview */}
          <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-3">
            <div className="flex flex-wrap gap-2">
              {displayUsers.map((u) => (
                <span
                  key={u.id}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white/[0.06] text-gray-300 text-xs"
                >
                  <i className="ri-user-line text-gray-500 text-[10px]"></i>
                  {u.full_name || u.email || "이름 없음"}
                </span>
              ))}
              {remaining > 0 && (
                <span className="inline-flex items-center px-2 py-1 rounded-lg bg-white/[0.06] text-gray-500 text-xs">
                  +{remaining}명
                </span>
              )}
            </div>
          </div>

          {/* Status options */}
          <div>
            <label className="block text-gray-400 text-xs font-medium mb-2">변경할 상태</label>
            <div className="grid grid-cols-1 gap-2">
              {STATUS_OPTIONS.map((opt) => {
                const isActive = selectedStatus === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => {
                      setSelectedStatus(opt.value);
                      setSaved(false);
                      setError(null);
                    }}
                    disabled={saving}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-left cursor-pointer transition-all ${
                      isActive
                        ? `${opt.bg} ${opt.border}`
                        : "bg-white/[0.02] border-white/[0.06] hover:border-white/[0.15]"
                    }`}
                  >
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 ${isActive ? opt.bg : "bg-white/[0.04]"}`}>
                      <i className={`${opt.icon} ${isActive ? opt.color : "text-gray-600"}`}></i>
                    </div>
                    <div>
                      <p className={`text-sm font-semibold ${isActive ? opt.color : "text-white"}`}>{opt.label}</p>
                      <p className="text-gray-600 text-[11px] mt-0.5">{opt.desc}</p>
                    </div>
                    {isActive && (
                      <i className="ri-check-line text-emerald-400 text-sm ml-auto"></i>
                    )}
                  </button>
                );
              })}
            </div>
            {error && (
              <p className="text-red-400 text-xs mt-2.5 flex items-center gap-1">
                <i className="ri-error-warning-line"></i>
                {error}
              </p>
            )}
            {saved && (
              <p className="text-emerald-400 text-xs mt-2.5 flex items-center gap-1">
                <i className="ri-checkbox-circle-line"></i>
                저장 완료
              </p>
            )}
          </div>

          {/* Confirm summary */}
          {selectedStatus && (
            <div className="bg-emerald-500/5 border border-emerald-500/10 rounded-xl p-3">
              <div className="flex items-center gap-2">
                <i className="ri-alert-line text-emerald-400 text-sm"></i>
                <p className="text-gray-400 text-xs">
                  <span className="text-white font-medium">{selectedUsers.length.toLocaleString()}명</span>의 회원 상태를
                  <span className="text-white font-medium"> {selectedLabel}</span>
                  (으)로 변경합니다.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-white/[0.08] shrink-0">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-white/[0.05] border border-white/[0.08] text-gray-300 hover:text-white text-sm font-medium cursor-pointer transition-colors"
          >
            취소
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !selectedStatus}
            className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-semibold rounded-lg cursor-pointer whitespace-nowrap transition-colors"
          >
            {saving ? (
              <span className="flex items-center gap-1.5">
                <i className="ri-loader-4-line animate-spin"></i>
                저장 중
              </span>
            ) : (
              <span className="flex items-center gap-1.5">
                <i className="ri-check-line"></i>
                적용
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}